import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Receipt as TaxIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Visibility as VisibilityIcon,
  ManageAccounts as ManageAccountsIcon,
  LockReset as LockResetIcon,
  Login as LoginIcon,
  MoreVert as MoreVertIcon,
  AddCircleOutline as AddonIcon,
} from '@mui/icons-material';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { getAllCompanies, updateCompany, getCompanyUsers, updateUserPassword, loginAsUser } from '../services/companyService';
import { storeUserData } from '../../services/authService';
import companySubscriptionService, { deactivateSubscription, reactivateSubscription } from '../services/companySubscriptionService';
import subscriptionAddonService from '../services/subscriptionAddonService';
import API_CONFIG from '../../config/api.config';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Helper function to get full logo URL
 */
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return '';
  // If logoUrl already starts with /api, construct full URL
  if (logoUrl.startsWith('/api')) {
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return `${baseUrl}${logoUrl}`;
  }
  // If it's already a full URL, return as is
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }
  // Otherwise, assume it's a relative path and construct URL
  const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
  return `${baseUrl}/api${logoUrl.startsWith('/') ? logoUrl : '/' + logoUrl}`;
};

/**
 * Format date - Simple format like database display
 */
const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';

  try {
    let date;

    // Handle array format from backend: [year, month, day, hour, minute, second]
    if (Array.isArray(dateValue)) {
      if (dateValue.length >= 6) {
        const [year, month, day, hour, minute, second] = dateValue;
        date = new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
      } else if (dateValue.length >= 3) {
        const [year, month, day] = dateValue;
        date = new Date(year, month - 1, day);
      } else {
        return 'N/A';
      }
    }
    // Handle string format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD" or ISO format
    else if (typeof dateValue === 'string') {
      // Handle MySQL DATETIME format: "YYYY-MM-DD HH:mm:ss"
      if (dateValue.includes(' ') && !dateValue.includes('T')) {
        date = new Date(dateValue.replace(' ', 'T'));
      }
      // Handle ISO format: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DDTHH:mm:ss.SSSZ"
      else if (dateValue.includes('T')) {
        date = new Date(dateValue);
      }
      // Handle date only: "YYYY-MM-DD"
      else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        date = new Date(dateValue + 'T00:00:00');
      }
      // Try parsing as is
      else {
        date = new Date(dateValue);
      }
    }
    // Handle Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    else {
      return 'N/A';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return 'N/A';
    }

    // Format date in a simple, readable format: "DD MMM YYYY" (without time)
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateValue);
    return 'N/A';
  }
};

/**
 * Calculate days until subscription ends
 */
const calculateDaysUntilEnd = (endDate) => {
  if (!endDate) return null;

  try {
    let date;
    if (Array.isArray(endDate) && endDate.length >= 6) {
      const [year, month, day] = endDate;
      date = new Date(year, month - 1, day);
    } else if (typeof endDate === 'string') {
      let isoDateString = endDate;
      if (endDate.includes(' ') && !endDate.includes('T')) {
        isoDateString = endDate.replace(' ', 'T');
      }
      date = new Date(isoDateString);
    } else if (endDate instanceof Date) {
      date = endDate;
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    console.error('Error calculating days until end:', error);
    return null;
  }
};

/**
 * Get button color based on days remaining
 */
const getButtonColor = (daysRemaining) => {
  if (daysRemaining === null) return '#5a9bd4'; // Default blue

  if (daysRemaining <= 3) {
    return '#f44336'; // Red
  } else if (daysRemaining <= 7) {
    return '#ff9800'; // Orange
  } else {
    return '#4caf50'; // Green
  }
};

/**
 * Client Card Component for Grid View
 */
const ClientCard = ({ company, subscription, onViewDetails, onManage, onAddon, navigate }) => {
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header with Logo and Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={getLogoUrl(company.logoUrl)}
            sx={{
              width: 64,
              height: 64,
              bgcolor: '#5a9bd4',
              color: '#fff',
              fontSize: '1.5rem',
            }}
          >
            {company.name?.charAt(0)?.toUpperCase() || 'C'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }} noWrap>
              {company.name || '-'}
            </Typography>
            {company.taxNumber && (
              <Chip
                icon={<TaxIcon />}
                label={company.taxNumber}
                size="small"
                sx={{
                  bgcolor: '#e8f4f8',
                  color: '#2c3e50',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Contact Information */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {company.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: 18, color: '#6c757d' }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {company.email}
              </Typography>
            </Box>
          )}

          {company.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: '#6c757d' }} />
              <Typography variant="body2" color="text.secondary">
                {company.phone}
              </Typography>
            </Box>
          )}

          {company.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationIcon sx={{ fontSize: 18, color: '#6c757d', mt: 0.5 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {company.address}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 100%' }}>
          Created: {formatDate(company.createdAt)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setCardMenuAnchor(e.currentTarget); }}
            sx={{ color: '#6c757d' }}
            aria-label="Actions"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={cardMenuAnchor}
            open={Boolean(cardMenuAnchor)}
            onClose={() => setCardMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {onManage && (
              <MenuItem onClick={() => { setCardMenuAnchor(null); onManage(company); }}>
                <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Manage</ListItemText>
              </MenuItem>
            )}
            {(() => {
              const hasSub = subscription && subscription.subscriptionEndDate;
              const active = subscription && subscription.active;
              const deactivated = hasSub && !active;
              if (active) {
                return (
                  <MenuItem onClick={() => { setCardMenuAnchor(null); onViewDetails(company.id); }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                  </MenuItem>
                );
              } else if (deactivated) {
                return (
                  <MenuItem onClick={() => { setCardMenuAnchor(null); onViewDetails(company.id); }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                  </MenuItem>
                );
              } else {
                return (
                  <MenuItem onClick={() => { setCardMenuAnchor(null); navigate(`/superadmin/clients/${company.id}/activate-subscription`); }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Activate Subscription</ListItemText>
                  </MenuItem>
                );
              }
            })()}
            {onAddon && (
              <MenuItem onClick={() => { setCardMenuAnchor(null); onAddon(company); }}>
                <ListItemIcon><AddonIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Add on</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </CardActions>
    </Card>
  );
};

/**
 * Clients Page
 * Display all clients from companies table
 */
const ClientsPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [subscriptions, setSubscriptions] = useState({}); // Map of companyId -> subscription
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [view, setView] = useState('list'); // 'list' or 'grid'
  const [filter, setFilter] = useState('all'); // 'all', 'free_trial', 'active_subscription', 'inactive'
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageCompany, setManageCompany] = useState(null);
  const [manageUsers, setManageUsers] = useState([]);
  const [manageUsersLoading, setManageUsersLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState(false);
  const [loginAsUserId, setLoginAsUserId] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuCompany, setActionMenuCompany] = useState(null);
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [addonDialogCompany, setAddonDialogCompany] = useState(null);
  const [addonDefinitions, setAddonDefinitions] = useState([]);
  const [addonDefinitionsLoading, setAddonDefinitionsLoading] = useState(false);
  const [addonForm, setAddonForm] = useState({ addonDefinitionId: '', quantityUnits: 1, durationType: 'REMAINING', notes: '' });
  const [addonSaving, setAddonSaving] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, [page, rowsPerPage, filter]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllCompanies(page, rowsPerPage, 'name', 'ASC', filter);
      const companiesList = response.content || [];
      setCompanies(companiesList);
      setTotalElements(response.totalElements || 0);

      // Fetch subscription data for each company
      const subscriptionMap = {};
      for (const company of companiesList) {
        try {
          const subscription = await companySubscriptionService.getCompanySubscription(company.id);
          if (subscription) {
            subscriptionMap[company.id] = subscription;
          }
        } catch (err) {
          // Subscription might not exist, that's okay
          console.log(`No subscription found for company ${company.id}`);
        }
      }
      setSubscriptions(subscriptionMap);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.message || 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleViewDetails = async (companyId) => {
    try {
      setSelectedCompanyId(companyId);
      const subscription = subscriptions[companyId];
      if (subscription) {
        setSelectedSubscription(subscription);
      } else {
        // Fetch subscription if not already loaded
        const sub = await companySubscriptionService.getCompanySubscription(companyId);
        setSelectedSubscription(sub);
        setSubscriptions(prev => ({ ...prev, [companyId]: sub }));
      }
      setSubscriptionDialogOpen(true);
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      setError('Failed to load subscription details');
    }
  };

  const handleCloseDialog = () => {
    setSubscriptionDialogOpen(false);
    setSelectedCompanyId(null);
    setSelectedSubscription(null);
  };

  const handleOpenManage = async (company) => {
    setManageCompany(company);
    setManageDialogOpen(true);
    setManageUsers([]);
    setManageUsersLoading(true);
    try {
      const users = await getCompanyUsers(company.id);
      setManageUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Error fetching company users:', err);
      toast.error(err.message || 'Failed to load users');
      setManageUsers([]);
    } finally {
      setManageUsersLoading(false);
    }
  };

  const handleCloseManage = () => {
    setManageDialogOpen(false);
    setManageCompany(null);
    setManageUsers([]);
  };

  const handleOpenActionMenu = (e, company) => {
    e.stopPropagation();
    setActionMenuAnchor(e.currentTarget);
    setActionMenuCompany(company);
  };
  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuCompany(null);
  };
  const handleOpenAddonDialog = async (company) => {
    handleCloseActionMenu();
    setAddonDialogCompany(company);
    setAddonForm({ addonDefinitionId: '', quantityUnits: 1, durationType: 'REMAINING', notes: '' });
    setAddonDialogOpen(true);
    setAddonDefinitionsLoading(true);
    try {
      const list = await subscriptionAddonService.listActiveAddonDefinitions();
      setAddonDefinitions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading addon definitions:', err);
      toast.error('Failed to load add-on options');
      setAddonDefinitions([]);
    } finally {
      setAddonDefinitionsLoading(false);
    }
  };
  const handleCloseAddonDialog = () => {
    if (!addonSaving) {
      setAddonDialogOpen(false);
      setAddonDialogCompany(null);
      setAddonForm({ addonDefinitionId: '', quantityUnits: 1, durationType: 'REMAINING', notes: '' });
    }
  };
  const handleAddonFormChange = (field, value) => {
    setAddonForm(prev => ({ ...prev, [field]: value }));
  };
  const handleAddAddon = async () => {
    if (!addonDialogCompany || !addonForm.addonDefinitionId) {
      toast.error('Please select an add-on');
      return;
    }
    const qty = parseInt(addonForm.quantityUnits, 10) || 1;
    if (qty < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    const def = addonDefinitions.find(d => d.id === parseInt(addonForm.addonDefinitionId, 10));
    const addonLabel = def ? `${qty} ${def.name}` : 'add-on';
    const companyName = addonDialogCompany.name || 'this company';
    const msg = `Confirm you are adding the ${addonLabel} to ${companyName}?`;
    if (!window.confirm(msg)) return;
    try {
      setAddonSaving(true);
      await subscriptionAddonService.addCompanyAddon({
        companyId: addonDialogCompany.id,
        addonDefinitionId: parseInt(addonForm.addonDefinitionId, 10),
        quantityUnits: qty,
        durationType: addonForm.durationType || 'REMAINING',
        notes: addonForm.notes || null,
      });
      toast.success(`Add-on added successfully for ${companyName}`);
      handleCloseAddonDialog();
    } catch (err) {
      console.error('Error adding add-on:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to add add-on');
    } finally {
      setAddonSaving(false);
    }
  };

  const handleOpenPasswordDialog = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUpdatePassword = async () => {
    if (!passwordUser || !newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setUpdatePasswordLoading(true);
    try {
      await updateUserPassword(passwordUser.id, newPassword);
      toast.success('Password updated successfully');
      handleClosePasswordDialog();
    } catch (err) {
      console.error('Error updating password:', err);
      toast.error(err.message || 'Failed to update password');
    } finally {
      setUpdatePasswordLoading(false);
    }
  };

  const handleLoginAsUser = async (user) => {
    setLoginAsUserId(user.id);
    try {
      const data = await loginAsUser(user.id);
      if (data?.accessToken && data?.user) {
        storeUserData(data);
        toast.success(`Opening webapp as ${data.user.name || data.user.email}`);
        window.location.href = '/';
      } else {
        toast.error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error logging in as user:', err);
      toast.error(err.message || 'Failed to open as user');
    } finally {
      setLoginAsUserId(null);
    }
  };

  const handleDeactivateSubscription = async () => {
    if (!selectedCompanyId) return;

    if (!window.confirm('Are you sure you want to deactivate this subscription? The company will not be able to login until reactivated.')) {
      return;
    }

    try {
      const response = await deactivateSubscription(selectedCompanyId);
      toast.success(response.message || 'Subscription deactivated successfully');

      // Refresh subscription data
      const updatedSub = await companySubscriptionService.getCompanySubscription(selectedCompanyId);
      setSelectedSubscription(updatedSub);
      setSubscriptions(prev => ({ ...prev, [selectedCompanyId]: updatedSub }));

      // Refresh companies list to update button colors
      fetchCompanies();
    } catch (err) {
      console.error('Error deactivating subscription:', err);
      toast.error(err.message || 'Failed to deactivate subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    if (!selectedCompanyId) return;

    if (!window.confirm('Are you sure you want to reactivate this subscription? The company will be able to login until the original end date.')) {
      return;
    }

    try {
      const response = await reactivateSubscription(selectedCompanyId);
      toast.success(response.message || 'Subscription reactivated successfully');

      // Refresh subscription data
      const updatedSub = await companySubscriptionService.getCompanySubscription(selectedCompanyId);
      setSelectedSubscription(updatedSub);
      setSubscriptions(prev => ({ ...prev, [selectedCompanyId]: updatedSub }));

      // Refresh companies list to update button colors
      fetchCompanies();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      toast.error(err.message || 'Failed to reactivate subscription');
    }
  };

  const handleToggleSessionLimit = async (company) => {
    try {
      // Toggle the value. If null/undefined, treat as true (default), so toggle to false.
      // Current: true -> new: false.
      // Current: false -> new: true.
      const currentVal = company.strictSessionLimit !== false; // Default is true
      const newVal = !currentVal;

      await updateCompany(company.id, {
        ...company,
        strictSessionLimit: newVal
      });

      // Update local state
      setCompanies(companies.map(c =>
        c.id === company.id ? { ...c, strictSessionLimit: newVal } : c
      ));

      toast.success(`Session limit ${newVal ? 'enabled' : 'disabled'} for ${company.name}`);
    } catch (err) {
      console.error('Error updating session limit:', err);
      toast.error('Failed to update session limit');
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(searchLower) ||
      company.email?.toLowerCase().includes(searchLower) ||
      company.phone?.toLowerCase().includes(searchLower) ||
      company.address?.toLowerCase().includes(searchLower) ||
      company.taxNumber?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
              All Clients
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and view all client information from companies table
            </Typography>
          </Box>
          {/* View Toggle */}
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => {
              if (newView !== null) {
                setView(newView);
              }
            }}
            size="small"
            sx={{ height: 36 }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Filters and Search Bar */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filter}
                  label="Filter by Status"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="all">All Clients</MenuItem>
                  <MenuItem value="free_trial">Free Trial</MenuItem>
                  <MenuItem value="active_subscription">Active Subscription</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={9}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search clients by name, email, phone, address, or tax number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Clients Display */}
        {loading ? (
          <Paper elevation={2} sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading clients...
            </Typography>
          </Paper>
        ) : filteredCompanies.length === 0 ? (
          <Paper elevation={2} sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? 'No clients found matching your search.' : 'No clients found.'}
            </Typography>
          </Paper>
        ) : view === 'list' ? (
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Logo</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Company Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Plan Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Single Session</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow
                      key={company.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                      }}
                    >
                      <TableCell>
                        <Avatar
                          src={getLogoUrl(company.logoUrl)}
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: '#5a9bd4',
                            color: '#fff',
                          }}
                        >
                          {company.name?.charAt(0)?.toUpperCase() || 'C'}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                          {company.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                          <Typography variant="body2" color="text.secondary">
                            {company.email || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                          <Typography variant="body2" color="text.secondary">
                            {company.phone || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {subscriptions[company.id]?.planName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={company.strictSessionLimit !== false ? "Strict Mode: Only 1 active session allowed" : "Multi-Login Allowed"}>
                          <Switch
                            checked={company.strictSessionLimit !== false}
                            onChange={() => handleToggleSessionLimit(company)}
                            color="primary"
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(company.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenActionMenu(e, company)}
                            sx={{ color: '#6c757d' }}
                            aria-label="Actions"
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor) && actionMenuCompany?.id === company.id}
                            onClose={handleCloseActionMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <MenuItem
                              onClick={() => {
                                handleCloseActionMenu();
                                handleOpenManage(company);
                              }}
                            >
                              <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                              <ListItemText>Manage</ListItemText>
                            </MenuItem>
                            {(() => {
                              const sub = subscriptions[company.id];
                              const hasSub = sub && sub.subscriptionEndDate;
                              const active = sub && sub.active;
                              const deactivated = hasSub && !active;
                              if (active) {
                                return (
                                  <MenuItem onClick={() => { handleCloseActionMenu(); handleViewDetails(company.id); }}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>View Details</ListItemText>
                                  </MenuItem>
                                );
                              } else if (deactivated) {
                                return (
                                  <MenuItem onClick={() => { handleCloseActionMenu(); handleViewDetails(company.id); }}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>View Details</ListItemText>
                                  </MenuItem>
                                );
                              } else {
                                return (
                                  <MenuItem onClick={() => { handleCloseActionMenu(); navigate(`/superadmin/clients/${company.id}/activate-subscription`); }}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>Activate Subscription</ListItemText>
                                  </MenuItem>
                                );
                              }
                            })()}
                            <MenuItem onClick={() => handleOpenAddonDialog(company)}>
                              <ListItemIcon><AddonIcon fontSize="small" /></ListItemIcon>
                              <ListItemText>Add on</ListItemText>
                            </MenuItem>
                          </Menu>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {companies.length > 0 && (
              <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                }}
              />
            )}
          </Paper>
        ) : (
          <>
            <Grid container spacing={3}>
              {filteredCompanies.map((company) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={company.id}>
                  <ClientCard
                    company={company}
                    subscription={subscriptions[company.id]}
                    onViewDetails={handleViewDetails}
                    onManage={handleOpenManage}
                    onAddon={handleOpenAddonDialog}
                    navigate={navigate}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Pagination for Grid View */}
            {companies.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                  component="div"
                  count={totalElements}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[12, 24, 48, 96]}
                />
              </Box>
            )}
          </>
        )}

        {/* Summary Info */}
        {!loading && companies.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? `Showing ${filteredCompanies.length} of ${companies.length} clients on this page (${totalElements} total)`
                : `Showing ${companies.length} of ${totalElements} clients`
              }
            </Typography>
          </Box>
        )}

        {/* Subscription Details Dialog */}
        <Dialog
          open={subscriptionDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Subscription Details
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedSubscription ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Plan Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.planName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Plan Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.planCode || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Billing Cycle
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.billingCycle ? selectedSubscription.billingCycle.charAt(0).toUpperCase() + selectedSubscription.billingCycle.slice(1) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Amount Paid
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.amountPaid
                        ? `${selectedSubscription.currency || 'INR'} ${parseFloat(selectedSubscription.amountPaid).toFixed(2)}`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Payment Status
                    </Typography>
                    <Chip
                      label={selectedSubscription.paymentStatus ? selectedSubscription.paymentStatus.charAt(0).toUpperCase() + selectedSubscription.paymentStatus.slice(1) : 'N/A'}
                      color={
                        selectedSubscription.paymentStatus === 'paid' ? 'success' :
                          selectedSubscription.paymentStatus === 'pending' ? 'warning' : 'default'
                      }
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Payment Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.paymentDate ? formatDate(selectedSubscription.paymentDate) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Subscription Start Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.subscriptionStartDate ? formatDate(selectedSubscription.subscriptionStartDate) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Subscription End Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                      {selectedSubscription.subscriptionEndDate ? formatDate(selectedSubscription.subscriptionEndDate) : 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedSubscription.subscriptionEndDate && (
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: getButtonColor(calculateDaysUntilEnd(selectedSubscription.subscriptionEndDate)) === '#f44336' ? '#ffebee' :
                          getButtonColor(calculateDaysUntilEnd(selectedSubscription.subscriptionEndDate)) === '#ff9800' ? '#fff3e0' :
                            '#e8f5e9'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Days Remaining: {calculateDaysUntilEnd(selectedSubscription.subscriptionEndDate) !== null
                            ? `${calculateDaysUntilEnd(selectedSubscription.subscriptionEndDate)} days`
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {selectedSubscription.paymentReference && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Payment Reference
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                        {selectedSubscription.paymentReference}
                      </Typography>
                    </Grid>
                  )}
                  {selectedSubscription.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {selectedSubscription.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading subscription details...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            {selectedSubscription && selectedSubscription.subscriptionEndDate && (
              <>
                {selectedSubscription.active ? (
                  <Button
                    onClick={handleDeactivateSubscription}
                    variant="outlined"
                    color="error"
                    sx={{ mr: 'auto' }}
                  >
                    Deactivate Subscription
                  </Button>
                ) : (
                  <Button
                    onClick={handleReactivateSubscription}
                    variant="contained"
                    color="success"
                    sx={{ mr: 'auto' }}
                  >
                    Continue Plan
                  </Button>
                )}
              </>
            )}
            <Button onClick={handleCloseDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add on dialog – add add-on for company (no payment) */}
        <Dialog open={addonDialogOpen} onClose={handleCloseAddonDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add on – {addonDialogCompany?.name || 'Company'}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an add-on and quantity. This will be added directly (no payment). Use when the client has paid offline or payment failed.
            </Typography>
            {addonDefinitionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Add-on</InputLabel>
                  <Select
                    value={addonForm.addonDefinitionId}
                    label="Add-on"
                    onChange={(e) => handleAddonFormChange('addonDefinitionId', e.target.value)}
                  >
                    <MenuItem value="">Select add-on</MenuItem>
                    {addonDefinitions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name} ({d.metricType}, {d.quantityPerUnit} per unit – {d.currency} {d.priceYearly}/year)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Quantity"
                  type="number"
                  size="small"
                  fullWidth
                  value={addonForm.quantityUnits}
                  onChange={(e) => handleAddonFormChange('quantityUnits', e.target.value)}
                  inputProps={{ min: 1 }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={addonForm.durationType}
                    label="Duration"
                    onChange={(e) => handleAddonFormChange('durationType', e.target.value)}
                  >
                    <MenuItem value="REMAINING">Until plan end (pro-rated)</MenuItem>
                    <MenuItem value="FULL_PERIOD">Full period from now</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Notes (optional)"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={addonForm.notes}
                  onChange={(e) => handleAddonFormChange('notes', e.target.value)}
                  placeholder="e.g. Added manually – payment received offline"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseAddonDialog} disabled={addonSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleAddAddon} disabled={addonSaving || !addonForm.addonDefinitionId}>
              {addonSaving ? 'Adding…' : 'Add add-on'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage company users dialog */}
        <Dialog open={manageDialogOpen} onClose={handleCloseManage} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Manage users – {manageCompany?.name || 'Company'}
            </Typography>
            <IconButton onClick={handleCloseManage} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {manageUsersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : manageUsers.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>
                No users found for this company.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manageUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>{user.email || '-'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={loginAsUserId === user.id ? <CircularProgress size={16} /> : <LoginIcon />}
                              onClick={() => handleLoginAsUser(user)}
                              disabled={loginAsUserId !== null}
                              sx={{ borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { borderColor: '#1b5e20', bgcolor: 'rgba(46,125,50,0.08)' } }}
                            >
                              Login as admin
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LockResetIcon />}
                              onClick={() => handleOpenPasswordDialog(user)}
                              sx={{ borderColor: '#5a9bd4', color: '#5a9bd4', '&:hover': { borderColor: '#4a8bc4', bgcolor: 'rgba(90,155,212,0.08)' } }}
                            >
                              Update password
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseManage} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update password dialog */}
        <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Update password
              {passwordUser && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                  – {passwordUser.name || passwordUser.email}
                </Typography>
              )}
            </Typography>
            <IconButton onClick={handleClosePasswordDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                autoComplete="new-password"
              />
              <TextField
                fullWidth
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClosePasswordDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpdatePassword}
              disabled={updatePasswordLoading || !newPassword.trim() || newPassword !== confirmPassword}
            >
              {updatePasswordLoading ? <CircularProgress size={24} /> : 'Update password'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
};

export default ClientsPage;

